import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { apiGet, apiPost, apiPatch, apiDelete } from '../../lib/api-client';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';

interface GroupItem {
  id: string;
  name: string;
  merchantIds: string[];
  createdAt: string;
}

interface MerchantItem {
  merchantId: string;
  code: string;
  name: string;
}

function DeleteGroupDialog({
  group,
  pending,
  onClose,
  onConfirm,
}: {
  group: GroupItem;
  pending: boolean;
  onClose: () => void;
  onConfirm: () => void;
}) {
  const { t } = useTranslation();
  const [text, setText] = useState('');
  const match = text.trim() === group.name;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
      <div className="mx-4 w-full max-w-md rounded-lg bg-white p-6 shadow-xl dark:bg-gray-800">
        <h3 className="mb-2 text-lg font-semibold text-gray-900 dark:text-gray-100">
          {t('mdr.deleteGroupTitle')}
        </h3>
        <p className="mb-4 text-sm text-gray-600 dark:text-gray-300">
          {t('mdr.deleteGroupPrompt', { name: group.name })}
        </p>
        <Input
          autoFocus
          value={text}
          onChange={(e) => setText(e.target.value)}
          placeholder={group.name}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && match && !pending) onConfirm();
          }}
        />
        <div className="mt-4 flex gap-2">
          <Button
            variant="destructive"
            className="flex-1"
            disabled={!match || pending}
            onClick={onConfirm}
          >
            {pending ? t('common.loading') : t('common.delete')}
          </Button>
          <Button variant="ghost" onClick={onClose}>
            {t('common.cancel')}
          </Button>
        </div>
      </div>
    </div>
  );
}

export function MdrGroups() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [name, setName] = useState('');
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [error, setError] = useState<string | null>(null);
  const [deleting, setDeleting] = useState<GroupItem | null>(null);

  const { data: groupsData, isLoading } = useQuery({
    queryKey: ['mdr', 'groups'],
    queryFn: () => apiGet<{ items: GroupItem[]; total: number }>('/mdr/groups'),
  });
  const { data: merchantsData } = useQuery({
    queryKey: ['mdr', 'merchants'],
    queryFn: () => apiGet<{ items: MerchantItem[]; total: number }>('/mdr/merchants'),
  });

  const openCreate = () => {
    setEditingId(null);
    setName('');
    setSelected(new Set());
    setError(null);
    setShowForm(true);
  };

  const openEdit = (g: GroupItem) => {
    setEditingId(g.id);
    setName(g.name);
    setSelected(new Set(g.merchantIds));
    setError(null);
    setShowForm(true);
  };

  const closeForm = () => {
    setShowForm(false);
    setEditingId(null);
  };

  const saveMutation = useMutation({
    mutationFn: () => {
      const body = { name, merchantIds: [...selected] };
      return editingId
        ? apiPatch<GroupItem>(`/mdr/groups/${editingId}`, body)
        : apiPost<GroupItem>('/mdr/groups', body);
    },
    onSuccess: (res) => {
      if (res.ok) {
        closeForm();
        queryClient.invalidateQueries({ queryKey: ['mdr', 'groups'] });
      } else {
        setError(res.error.message);
      }
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => apiDelete(`/mdr/groups/${id}`),
    onSuccess: () => {
      setDeleting(null);
      queryClient.invalidateQueries({ queryKey: ['mdr', 'groups'] });
    },
  });

  const groups = groupsData?.ok ? groupsData.data.items : [];
  const merchants = merchantsData?.ok ? merchantsData.data.items : [];
  const nameByMerchant = new Map(merchants.map((m) => [m.merchantId, m.name]));

  const toggle = (id: string) =>
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold text-gray-800 dark:text-gray-100">
          {t('mdr.groupsTitle')}
        </h1>
        <Button size="sm" onClick={openCreate}>
          {t('mdr.createGroup')}
        </Button>
      </div>

      {showForm && (
        <Card>
          <CardHeader>
            <CardTitle>{editingId ? t('mdr.editGroup') : t('mdr.createGroup')}</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <Input
              placeholder={t('mdr.groupName')}
              value={name}
              onChange={(e) => setName(e.target.value)}
            />
            <p className="text-xs text-gray-500">{t('mdr.selectMerchants')}</p>
            <div className="max-h-60 space-y-1 overflow-y-auto rounded-md border border-gray-200 p-2 dark:border-gray-700">
              {merchants.map((m) => (
                <label key={m.merchantId} className="flex items-center gap-2 text-sm">
                  <input
                    type="checkbox"
                    checked={selected.has(m.merchantId)}
                    onChange={() => toggle(m.merchantId)}
                  />
                  <span className="font-mono text-xs text-gray-500">{m.code}</span>
                  <span>{m.name}</span>
                </label>
              ))}
              {merchants.length === 0 && (
                <p className="text-sm text-gray-400">{t('common.noData')}</p>
              )}
            </div>
            {error && <p className="text-sm text-red-600">{error}</p>}
            <div className="flex gap-2">
              <Button
                size="sm"
                onClick={() => saveMutation.mutate()}
                disabled={saveMutation.isPending || !name}
              >
                {t('common.save')}
              </Button>
              <Button size="sm" variant="ghost" onClick={closeForm}>
                {t('common.cancel')}
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {isLoading && <div className="text-gray-400">{t('common.loading')}</div>}

      <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3">
        {groups.map((g) => (
          <Card key={g.id}>
            <CardHeader>
              <CardTitle>{g.name}</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="mb-3 flex flex-wrap gap-2">
                <Button size="sm" variant="outline" onClick={() => navigate(`/mdr/groups/${g.id}`)}>
                  {t('common.view')}
                </Button>
                <Button size="sm" variant="outline" onClick={() => openEdit(g)}>
                  {t('common.edit')}
                </Button>
                <Button size="sm" variant="destructive" onClick={() => setDeleting(g)}>
                  {t('common.delete')}
                </Button>
              </div>
              <p className="mb-2 text-xs text-gray-500">
                {t('mdr.merchantCount')}: {g.merchantIds.length}
              </p>
              <ul className="space-y-1 text-sm">
                {g.merchantIds.slice(0, 8).map((id) => (
                  <li key={id} className="truncate text-gray-600 dark:text-gray-300">
                    {nameByMerchant.get(id) ?? id}
                  </li>
                ))}
                {g.merchantIds.length > 8 && (
                  <li className="text-xs text-gray-400">+{g.merchantIds.length - 8}…</li>
                )}
              </ul>
            </CardContent>
          </Card>
        ))}
      </div>

      {deleting && (
        <DeleteGroupDialog
          group={deleting}
          pending={deleteMutation.isPending}
          onClose={() => setDeleting(null)}
          onConfirm={() => deleteMutation.mutate(deleting.id)}
        />
      )}
    </div>
  );
}
